import { FC } from "react";
import { Box, VBox } from "../../components-v2/Box";
import { observer } from "mobx-react";
import {
  ApproveCompanyBoardRemunerationForm,
  RemunerationViewModel,
} from "./approveCompanyBoardRemunerationDomain";
import { FromToPeriod, NumberField, YesNoQuestion } from "../common/commonComponents";
import { Checkbox } from "../../components-v2/Checkbox";
import { CURRENCY_NOK } from "../../utils/currency";
import { Alert } from "../../components-v2/Alert";

import { useTranslation } from "../../stores/RootStoreContext";
import { NumberFieldModel } from "../../common/commonForms";

const RemunerationValueInput = observer(function ({
  field,
  isProjectLocked,
}: {
  field: NumberFieldModel;
  isProjectLocked?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <NumberField
      label={t("decisions.enter-amount")}
      prefix={CURRENCY_NOK}
      field={field}
      isLocked={isProjectLocked}
    />
  );
});

const RemunerationView = observer(function ({
  remuneration,
  isProjectLocked,
  onToggle,
}: {
  remuneration: RemunerationViewModel;
  isProjectLocked?: boolean;
  onToggle: (remuneration: RemunerationViewModel) => void;
}) {
  return (
    <VBox marginLeft={"md"}>
      <Box>
        <Checkbox
          label={remuneration.name}
          checked={remuneration.isSelected}
          onChange={() => onToggle(remuneration)}
          disabled={isProjectLocked}
        />
      </Box>
      {remuneration.isSelected && (
        <RemunerationValueInput field={remuneration.value} isProjectLocked={isProjectLocked} />
      )}
    </VBox>
  );
});

export const ApprovalOfCompanyBoardRemunerationView: FC<{
  form: ApproveCompanyBoardRemunerationForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <YesNoQuestion
        question={t("decisions.approveCompanyBoardRemuneration.who-receive-remuneration.question")}
        field={form.isReceive}
        isLocked={form.isProjectLocked}
      />
      {form.showRemunerations && (
        <>
          <VBox>
            {form.remunerations.map((r) => (
              <RemunerationView
                key={r.id}
                remuneration={r}
                onToggle={form.toggleRemuneration}
                isProjectLocked={form.isProjectLocked}
              />
            ))}
            {form.boardNotSelectedWarning && (
              <Alert message={t(form.boardNotSelectedWarning)} type={"warning"} />
            )}
          </VBox>
          <YesNoQuestion
            question={t("decisions.approveCompanyBoardRemuneration.what-period.question")}
            field={form.isCurrentYear}
            isLocked={form.isProjectLocked}
          />
          {form.showPeriod && (
            <VBox>
              <FromToPeriod
                label={t("decisions.approveCompanyBoardRemuneration.enter-period")}
                from={form.deviatingPeriodFrom}
                to={form.deviatingPeriodTo}
                isLocked={form.isProjectLocked}
              />
              {form.deviatingPeriodSuitableWarning && (
                <Alert message={t(form.deviatingPeriodSuitableWarning)} type={"warning"} />
              )}
            </VBox>
          )}
        </>
      )}
    </VBox>
  );
});
